import { Jobibot } from '@/components/JobiBot.tsx'
import { LanguageSelector } from '@/components/LanguageSelector.tsx'
import { logger } from '@jobilla/instrumentation'
import { useCandidateAuthRequest } from '@jobilla/nexus-sdk/lib/hooks/useCandidateAuth'
import { useCandidateOrganisationIndex } from '@jobilla/nexus-sdk/lib/hooks/useCandidateOrganisation'
import { requestLoginLinkInputDataSchema } from '@jobilla/nexus-sdk/lib/zod/RequestLoginLinkInputDataSchema'
import { useForm } from '@tanstack/react-form'
import { Navigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Centered } from './Centered'

export function LoginForm({ heading, organisationName, linkResent = false, onDismissLinkResent }: {
	heading: string
	organisationName?: string
	linkResent?: boolean
	onDismissLinkResent?: () => void
}) {
	const { t } = useTranslation('common')
	const requestLink = useCandidateAuthRequest({
		onSuccess: () =>
			logger.track('Candidate Center: Requested login link', {
				data: { has_organisation_context: Boolean(organisationName) },
			}),
	})

	// An existing session makes the login page redundant — send them to their applications.
	const session = useCandidateOrganisationIndex({ retry: 0 })

	const form = useForm({
		defaultValues: { email: '' },
		validators: { onChange: requestLoginLinkInputDataSchema },
		onSubmit: ({ value }) => {
			requestLink.mutate({ data: { email: value.email.trim() } })
		},
	})

	if (session.isSuccess) {
		return <Navigate to="/organisations" replace={true} />
	}

	if (session.isPending) {
		return (
			<Centered>
				<p className="text-sm text-grey-500">{t('login.loading')}</p>
			</Centered>
		)
	}

	if (linkResent) {
		return (
			<Centered>
				<div className="w-full max-w-sm">
					<h1 className="text-xl font-medium text-grey-800">{t('login.resent.title')}</h1>
					<p className="mt-2 text-sm text-grey-500">{t('login.resent.body')}</p>
					<button
						type="button"
						onClick={onDismissLinkResent}
						className="mt-6 cursor-pointer text-sm font-medium text-brand-600 hover:text-brand-700"
					>
						{t('login.resent.request_manually')}
					</button>
				</div>
			</Centered>
		)
	}

	if (requestLink.isSuccess) {
		return (
			<Centered>
				<div className="w-full max-w-sm">
					{organisationName ? <p className="text-sm text-grey-500">{organisationName}</p> : null}
					<h1 className="mt-1 text-xl font-medium text-grey-800">{t('login.sent.title')}</h1>
					<p className="mt-2 text-sm text-grey-500">
						{t('login.sent.body', { email: form.state.values.email.trim() })}
					</p>
					<button
						type="button"
						onClick={() => {
							requestLink.reset()
							form.reset()
						}}
						className="mt-6 cursor-pointer text-sm font-medium text-brand-600 hover:text-brand-700"
					>
						{t('login.sent.retry')}
					</button>
				</div>
			</Centered>
		)
	}

	const requestStatus = requestLink.error?.response?.status
	const requestErrorKey = requestStatus === 429
		? 'login.request_error.throttled'
		: requestStatus === 422
		? 'login.request_error.invalid_email'
		: 'login.request_error.generic'

	return (
		<Centered>
			<div className="w-full max-w-sm text-left">
				<div className="mx-auto w-full flex items-center justify-center mb-4">
					<Jobibot size={32} />
				</div>
				<h1 className="text-center text-xl font-medium text-grey-800">{heading}</h1>
				<p className="mt-2 text-center text-sm text-grey-500">{t('login.intro')}</p>

				<form
					onSubmit={(event) => {
						event.preventDefault()
						void form.handleSubmit()
					}}
					className="mt-8 flex flex-col gap-2"
				>
					<label htmlFor="email" className="text-sm font-medium text-grey-700">
						{t('login.email_label')}
					</label>
					<form.Field name="email">
						{({ name, state, handleChange, handleBlur }) => (
							<>
								<input
									id="email"
									type="email"
									name={name}
									autoComplete="email"
									value={state.value}
									onBlur={handleBlur}
									onChange={(event) => handleChange(event.target.value)}
									placeholder={t('login.email_placeholder')}
									className="rounded-lg border border-grey-300 px-3 py-2 text-sm text-grey-800 placeholder:text-grey-400 focus:border-brand-500 focus:outline-none"
								/>
								{state.meta.isTouched && state.meta.errors.length > 0
									? (
										<p role="alert" className="text-sm text-red-600">
											{t('login.request_error.invalid_email')}
										</p>
									)
									: null}
							</>
						)}
					</form.Field>
					{requestLink.isError
						? <p role="alert" className="text-sm text-red-600">{t(requestErrorKey)}</p>
						: null}
					<form.Subscribe selector={(state) => ({ canSubmit: state.canSubmit, email: state.values.email })}>
						{({ canSubmit, email }) => (
							<button
								type="submit"
								disabled={!canSubmit || email.trim() === '' || requestLink.isPending}
								className="mt-2 cursor-pointer rounded-lg bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-grey-200 disabled:text-grey-400"
							>
								{requestLink.isPending ? t('login.sending') : t('login.submit')}
							</button>
						)}
					</form.Subscribe>
				</form>

				<div className="mt-8 flex justify-center">
					<LanguageSelector />
				</div>
			</div>
		</Centered>
	)
}
